import { getSpecialtyOnboardingV2Assets } from '@/pages/api/scribeDemoApi';
import { DEFAULT_LOCALE } from '@/utils/i18n/supportedLocales';
import { type OnboardingV2AssetsResponse } from '@heidi/growth';
import { useQuery } from '@tanstack/react-query';

export const GET_SPECIALTY_ONBOARDING_V2_ASSETS = 'getSpecialtyOnboardingV2Assets';

/**
 * Fetch all six onboarding-v2 specialty assets (transcript, note, abbreviations,
 * evidence query/response, document) for a canonical FE/Mongo specialty name.
 *
 * Backed by `GET /api/v1/ml-scribe/specialties/{name}/onboarding-v2-assets`,
 * which returns markdown content from per-locale `<locale>.json` bundles in the
 * BE repo (see `service/onboarding_v2/` in ml-scribe). The endpoint resolves
 * canonical names through an alias map and falls back to default content for
 * specialties without per-specialty entries, so this hook never 404s for a
 * specialty name returned by `useQuerySpecialties`.
 *
 * `locale` is the user's active display language (BCP-47, e.g. `es-ES`); the BE
 * loads the matching `<locale>.json` and falls back to English for unknown
 * locales. Passing it explicitly mirrors `useQuerySpecialtyTranscript`. It is
 * part of the query key so switching language refetches the localized content.
 *
 * Asset content is static per release per locale — `staleTime: Infinity` avoids
 * needless refetches as users navigate between sub-steps of the flow.
 */
export function useQuerySpecialtyOnboardingV2Assets(
  specialtyName: string | null | undefined,
  locale: string = DEFAULT_LOCALE
) {
  return useQuery<OnboardingV2AssetsResponse>({
    queryKey: [GET_SPECIALTY_ONBOARDING_V2_ASSETS, specialtyName, locale],
    queryFn: async () => {
      const { data } = await getSpecialtyOnboardingV2Assets(specialtyName!, locale);
      return data;
    },
    enabled: !!specialtyName,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
